/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  Input,
  Switch,
  DatePicker,
  message,
} from 'antd';
import Table from 'components/Table';
import { getEstadosGlobales } from 'api/catalogos';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import {
  getLiquidacionesConfiguracionesIndemnizaciones,
  getLiquidacionConfiguracionIndemnizacion,
  postLiquidacionConfiguracionIndemnizacion,
  patchLiquidacionConfiguracionIndemnizacion,
  deleteLiquidacionConfiguracionIndemnizacion,
  autorizarLiquidacionConfiguracionIndemnizacion,
  cancelarLiquidacionConfiguracionIndemnizacion,
  reactivarLiquidacionConfiguracionIndemnizacion,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesIndemnizacion';
import { getLiquidacionesUnidades } from 'api/nominas/liquidaciones/tipoLiquidacionUnidad';
import { ESTATUS_AUTORIZADO, ESTATUS_ACTIVO, ESTATUS_CANCELADO } from 'utils/constants';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import LiquidacionConfiguracionPercepcionFechaRelacionada, { liquidacionConfiguracionPercepcionFechaRelacionadaPermission } from './LiquidacionConfiguracionPercepcionFechaRelacionada';

export const liquidacionConfiguracionIndemnizacionPermission = {
  permissionModel: 'liquidacionconfiguracionpercepcion',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionConfiguracionIndemnizacion = ({ permission, liquidacionConfiguracion }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [percepciones, setPercecpiones] = useState([]);
  const [todasPercepciones, setTodasPercepciones] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [percepcionesFiltered, setPercepcionesFiltered] = useState([]);
  const [liquidacionesUnidades, setLiquidacionesUnidades] = useState([]);
  const [percepcionDelete, setPercepcionDelete] = useState();
  const [requierePercepcionRelacionada, setRequierePercepcionRelacionada] = useState(false);
  const [percepcionRelacionadaId, setPercepcionRelacionadaId] = useState(null);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [visibleCancelarConfirmation, setVisibleCancelarConfirmation] = useState(false);
  const [visibleReactivarConfirmation, setVisibleReactivarConfirmation] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesIndemnizaciones({ es_finiquito: false });
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  const filterPercepciones = (_percepciones) => {
    const _percepcionesFiltered = _percepciones.filter((per) => !data.some((val) => val?.percepcion === per?.id));
    return _percepcionesFiltered;
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _percepciones,
        _todasPercepciones,
        _liquidacionesUnidades,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPercepciones({ estados_globales: ESTATUS_AUTORIZADO, es_percepcion_de_indemnizacion: true }),
        getPercepciones({ estados_globales: ESTATUS_AUTORIZADO }),
        getLiquidacionesUnidades(),
        getLiquidacionesConfiguracionesIndemnizaciones({ es_finiquito: false }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPercecpiones(_percepciones);
        setTodasPercepciones(_todasPercepciones);
        setLiquidacionesUnidades(_liquidacionesUnidades);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    setPercepcionesFiltered(filterPercepciones(percepciones));
    setRequierePercepcionRelacionada(false);
    setPercepcionRelacionadaId(null);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionIndemnizacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    // El doble clic en una fila llama a esta funcion directo, sin pasar por el
    // boton "Editar" del toolbar (ver onRow.onDoubleClick en components/Table.jsx).
    if (match.estados_globales === ESTATUS_AUTORIZADO || match.estados_globales === ESTATUS_CANCELADO) {
      setLoading(false);
      message.warn('No se puede editar una Percepción de Indemnización Autorizada o Cancelada.');
      return;
    }
    const liquidacionConfigIndemnizacion = await getLiquidacionConfiguracionIndemnizacion(match.id);
    const percepcion = percepciones.find((e) => e.id === liquidacionConfigIndemnizacion?.percepcion);
    const _percepcionesFiltered = filterPercepciones(percepciones);
    setTimeout(() => {
      if (liquidacionConfigIndemnizacion) {
        form.resetFields();
        setRequierePercepcionRelacionada(liquidacionConfigIndemnizacion?.requiere_percepcion_relacionada || false);
        setPercepcionRelacionadaId(liquidacionConfigIndemnizacion?.percepcion_relacionada || null);
        setTimeout(() => {
          const rec = liquidacionConfigIndemnizacion;
          form.setFieldsValue({
            ...formatReceivedClone(rec),
            percepcion: percepcion?.id || null,
            rango_inicial: rec?.rango_inicial ? moment(`2000-${rec.rango_inicial}`, 'YYYY-MM-DD') : null,
            rango_final: rec?.rango_final ? moment(`2000-${rec.rango_final}`, 'YYYY-MM-DD') : null,
          });
          setPercepcionesFiltered(_percepcionesFiltered.concat(percepcion));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const rawValues = form.getFieldsValue();
      const values = {
        ...rawValues,
        es_finiquito: false,
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        rango_inicial: rawValues.rango_inicial ? rawValues.rango_inicial.format('MM-DD') : null,
        rango_final: rawValues.rango_final ? rawValues.rango_final.format('MM-DD') : null,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionIndemnizacion(key, values)
        : postLiquidacionConfiguracionIndemnizacion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickDelete = () => {
    setPercepcionDelete(percepciones.find((val) => val.id === parseInt(form.getFieldValue('percepcion'), 10))?.descripcion);
    setVisibleAlert(true);
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key) {
        const autorizado = await autorizarLiquidacionConfiguracionIndemnizacion(key);
        if (autorizado) {
          setConfirmationVisible(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key) {
        const cancelado = await cancelarLiquidacionConfiguracionIndemnizacion(key);
        if (cancelado) {
          setVisibleCancelarConfirmation(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key) {
        const reactivado = await reactivarLiquidacionConfiguracionIndemnizacion(key);
        if (reactivado) {
          setVisibleReactivarConfirmation(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (value) => {
        const _percepcion = percepciones.find((percepcion) => percepcion.id === value);
        return _percepcion ? `${_percepcion?.clave} - ${_percepcion?.descripcion}` : '';
      },
    },
    {
      titleText: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 300,
    },
    {
      titleText: 'Unidad de Liquidación',
      dataIndex: 'tipo_de_liquidacion_unidad',
      key: 'tipo_de_liquidacion_unidad',
      width: 300,
      render: (val) => liquidacionesUnidades.find((unidades) => unidades.id === val)?.descripcion,
    },
    {
      titleText: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 300,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 300,
    },
    {
      titleText: 'Rango Inicial',
      dataIndex: 'rango_inicial',
      key: 'rango_inicial',
      width: 130,
    },
    {
      titleText: 'Rango Final',
      dataIndex: 'rango_final',
      key: 'rango_final',
      width: 130,
    },
    {
      titleText: 'Req. Percepción Relacionada',
      dataIndex: 'requiere_percepcion_relacionada',
      key: 'requiere_percepcion_relacionada',
      width: 220,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Percepción Relacionada',
      dataIndex: 'percepcion_relacionada',
      key: 'percepcion_relacionada',
      width: 300,
      render: (value) => {
        const _percepcion = todasPercepciones.find((p) => p.id === value);
        return _percepcion ? `${_percepcion?.clave} - ${_percepcion?.descripcion}` : '';
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'liquidacionconfiguracionpercepcion', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO,
            },
            {
              onClick: () => setVisibleCancelarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionpercepcion', action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              className: 'cancel',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO,
            },
            {
              onClick: () => setVisibleReactivarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionpercepcion', action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_ACTIVO,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
          disableEdit={!selectedRowKeys?.length
            || [ESTATUS_AUTORIZADO, ESTATUS_CANCELADO].includes(
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales,
            )}
          disableDelete={!selectedRowKeys?.length
            || [ESTATUS_AUTORIZADO, ESTATUS_CANCELADO].includes(
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales,
            )}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={550}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Percepción de Indemnización`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  name="percepcion"
                  label="Percepción"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    dataSource={percepcionesFiltered}
                    render={(val) => `${val?.clave} - ${val?.descripcion}`}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="cantidad"
                  label="Cantidad"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="tipo_de_liquidacion_unidad"
                  label="Unidad de Liquidación"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    allowClear
                    dataSource={liquidacionesUnidades}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="referencia"
                  label="Referencia"
                  hasFeedback
                >
                  <Input
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="observaciones"
                  label="Observaciones"
                  hasFeedback
                >
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="rango_inicial"
                  label="Rango Inicial (MM-DD)"
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="MM-DD"
                    placeholder="Mes-Día"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="rango_final"
                  label="Rango Final (MM-DD)"
                  hasFeedback
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="MM-DD"
                    placeholder="Mes-Día"
                  />
                </Form.Item>
              </Col>
              <LiquidacionConfiguracionPercepcionFechaRelacionada
                percepcionRelacionadaId={percepcionRelacionadaId}
                requierePercepcionRelacionada={requierePercepcionRelacionada}
                permission={liquidacionConfiguracionPercepcionFechaRelacionadaPermission}
              />

              <Col span={24}>
                <Form.Item
                  name="requiere_percepcion_relacionada"
                  label="Requiere Percepción Relacionada"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch
                    onChange={(checked) => {
                      setRequierePercepcionRelacionada(checked);
                      if (!checked) form.setFieldsValue({ percepcion_relacionada: null });
                    }}
                  />
                </Form.Item>
              </Col>
              {requierePercepcionRelacionada && (
                <Col span={24}>
                  <Form.Item
                    name="percepcion_relacionada"
                    label="Percepción Relacionada"
                    rules={[{ required: true, message: 'El campo es requerido' }]}
                    hasFeedback
                  >
                    <Select
                      allowClear
                      dataSource={todasPercepciones}
                      render={(val) => `${val?.clave} - ${val?.descripcion}`}
                      onChange={(val) => setPercepcionRelacionadaId(val || null)}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Percepción ${percepcionDelete}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setConfirmationVisible(false)}
          open={confirmationVisible}
          contentTitle="¿Estás seguro de autorizar este registro?"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelarConfirmation(false)}
          open={visibleCancelarConfirmation}
          contentTitle="¿Estás seguro de cancelar este registro?"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivarConfirmation(false)}
          open={visibleReactivarConfirmation}
          contentTitle="¿Estás seguro de reactivar este registro?"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionConfiguracionIndemnizacion;
